import { Action } from "redux";
import { AppError } from "../../model/AppError";
import { Authentication } from "../../model/Authentication";
import { InProgress } from "../../model/InProgress";
import { ThunkAction } from "redux-thunk";
import { AppState } from "../store/AppState";
import { LogoutCompleted } from "../../model/LogoutCompleted";
import {
  LicenseWithOwnerAndSingleUserAssignments
} from "../../model/entitlement/LicenseWithOwnerAndSingleUserAssignments";
import {LicenseAssignment} from "../../model/entitlement/LicenseAssignment";
import {QueryAvailableLicensesOpts} from "../../model/entitlement/QueryAvailableLicensesOpts";

// base interface for actions of this application
export type AppAction = Action<string>;

// thunk action type for thunk that returns given action type or AddErrorAction
export type AppThunkAction<A extends AppAction> = ThunkAction<
  Promise<A | AddErrorAction<A>>,
  AppState,
  unknown,
  AppAction
>;

/*
 * action types (used as "type" field value in Redux actions) and interfaces describing each action
 */

// pseudo-action object used for carrying data of multiple actions
export const MULTI_ACTION = "MULTI_ACTION";
export type ActionResult<A extends AppAction> = A | AddErrorAction<A>;
export interface MultiAction extends AppAction {
  // results of the actual actions carried out
  results: ActionResult<AppAction>[];
}

// add error info
export const ADD_ERROR = "ADD_ERROR";
export interface AddErrorAction<A extends AppAction> extends AppAction {
  error: AppError<A>;
}
export function isAddErrorAction<A extends AppAction>(
  obj: any
): obj is AddErrorAction<A> {
  return "type" in obj && obj["type"] === ADD_ERROR;
}

// remove error with the given error id from the application state
export const CLEAR_ERROR = "CLEAR_ERROR";
export interface ClearErrorAction extends AppAction {
  errorId: string;
}

// remove all errors from the application state
export const CLEAR_ALL_ERRORS = "CLEAR_ALL_ERRORS";
export interface ClearAllErrorsAction extends AppAction {}

// add in-progress info
export const ADD_IN_PROGRESS = "ADD_IN_PROGRESS";
export interface AddInProgressAction<A extends AppAction> extends AppAction {
  operation: InProgress<A>;
}

// remove in-progress info with the given operation id from the application state
export const CLEAR_IN_PROGRESS = "CLEAR_IN_PROGRESS";
export interface ClearInProgressAction extends AppAction {
  operationId: string;
}

// remove all in-progress infos from the application state
export const CLEAR_ALL_IN_PROGRESS_INFOS = "CLEAR_ALL_IN_PROGRESS_INFOS";
export interface ClearAllInProgressInfosAction extends AppAction {}

// request clearing the whole redux store, mainly for test purposes
export const CLEAR_ALL = "CLEAR_ALL";
export interface ClearAllAction extends AppAction {}

// start authentication process
export const START_AUTHN = "START_AUTHN";
export interface StartAuthnAction extends AppAction {
    /**
     * URL where the browser must be directed for starting the authentication process.
     */
    url: URL;
    /**
     * PKCE code verifier for securing the authentication process.
     */
    codeVerifier: string;
    /**
     * OpenID Connect nonce used for securing the authentication process.
     */
    nonce: string;
    /**
     * Nonce issued timestamp.
     */
    nonceIssuedAt: number;
}

// set authenticated user
export const SET_AUTHN = "SET_AUTHN";
export interface SetAuthnAction extends AppAction {
  authn: Authentication;
}

// set logout completed info
export const SET_LOGOUT_COMPLETED = "LOGOUT_COMPLETED";
export interface SetLogoutCompletedAction extends AppAction {
  logoutCompleted: LogoutCompleted;
}

// clears states related to user authentication and pending authentication
export const CLEAR_AUTHN_STATUS = "CLEAR_AUTHN_STATUS";
export interface ClearAuthnStatusAction extends AppAction {}


export interface LicensesAction extends AppAction {
  licenses: LicenseWithOwnerAndSingleUserAssignments[];
}
export interface LicenseAssignmentAction extends AppAction {
  licenseAssignments: LicenseAssignment[];
}
export const QUERY_AVAILABLE_LICENSES = "QUERY_AVAILABLE_LICENSES";
export interface QueryAvailableLicensesAction
    extends LicensesAction,
        LicenseAssignmentAction {
  userId: string;
  queryAvailableLicensesOpts?: QueryAvailableLicensesOpts;
}
